import React from "react";
import styled from "styled-components";

const Page = styled.div`
  min-height: 100vh;
  background: #000; /* mimic x.company dark background */
  color: #fff;
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 96px 24px 64px 24px;

  @media (max-width: 768px) {
    padding: 72px 16px 48px 16px;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 800;
  margin: 0 0 32px 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  grid-auto-rows: 1fr;
  gap: 24px;

  /* Mobile: 3 columns like the reference */
  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr); /* 3 columns on mobile like reference */
    gap: 12px;
  }
`;

const TileLink = styled.a`
  position: relative;
  display: block;
  width: 100%;
  padding-top: 100%; /* desktop/tablet: square */
  border-radius: 16px;
  overflow: hidden;
  background: transparent;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.35);
  transition: transform 160ms ease, box-shadow 160ms ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.6);
  }

  /* Mobile: square cards */
  @media (max-width: 768px) {
    padding-top: 100%;
  }
`;

const ColorOverlay = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 16px;
  background: rgba(0, 0, 0, 0.9);
  transform: translateY(100%);
  transition: transform 260ms ease;
  display: flex;
  align-items: flex-end;
  padding: 14px;

  ${TileLink}:hover & {
    transform: translateY(0%);
  }
`;

const Img = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Caption = styled.div`
  color: #fff;
  font-size: 1.05rem;
  font-weight: 800;
  line-height: 1.25;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
`;

// Minimal subset of tiles to demonstrate the layout.
const TILES = [
  {
    title: "self-driving cars",
    href: "https://x.company/projects/waymo",
    color: "#95EDCA",
    img: "https://lh3.googleusercontent.com/faxtCqVNVWA6-xfiXvLk0GUfARiPMivH_EqSBRddPjHCJThVPEGpOouf5fRXx8nU6prVhVz1UJ2SyoKxSCmfBs9HB1sHrC_IM_98=e365-pa-nu-s0",
    colStart: 6,
    rowStart: 1,
  },
  {
    title: "snow globes for carbon capture",
    href: "https://x.company/projects/280earth",
    color: "#3714DE",
    img: "https://lh3.googleusercontent.com/b7qU5SZatCDFTs9mZcx58ut9plUDTMxpbD9lqeTcyIsEIwbyBpj7HNIVCEHRzYDX5wyrm7_yfDjmeeYr46x4gpmjfvHzOfuqL2dm=e365-pa-nu-s0",
    colStart: 4,
    rowStart: 2,
  },
  {
    title: "light-beamed internet",
    href: "https://x.company/projects/taara",
    color: "#F0F0F0",
    img: "https://lh3.googleusercontent.com/N0XV8h1mcBtuncWdk52vKlsv3YftAXoJnbH0P2Wt-f0dsfwYw2E9ac2tTHSU2mknwUlNYRe3t0HDVxKnMo59eebHdoIDMBt1Sn1c=e365-pa-nu-s0",
    colStart: 5,
    rowStart: 2,
  },
  {
    title: "molten salt for energy storage",
    href: "https://x.company/projects/malta",
    color: "#282C3D",
    img: "https://lh3.googleusercontent.com/S1RjmAu76veZc648NrTaoFY9H5h6pG7wjnfDSEoiymoF5PBdIwbn8Jioh9DJBAPKNLkpr6Sx4UCLL1umkjT4-KBS354GU8eA-HOXpw=e365-pa-nu-s0",
    colStart: 6,
    rowStart: 2,
  },
  {
    title: "a digital immune system for cybersecurity",
    href: "https://x.company/projects/chronicle/",
    color: "#282C3D",
    img: "https://lh3.googleusercontent.com/3Hw9diUlcZb3UovkmqlExTeHBc-vqUf5vpHbxM1nmXwLFfxHsvNFuqxAsjbIEWhaO1Q0qmjPbd8JwxIrUxwbnx_oBIcmtrw-cMJCVw=e365-pa-nu-s0",
    colStart: 1,
    rowStart: 3,
  },
  {
    title: "airborne package delivery",
    href: "https://x.company/projects/wing",
    color: "#CFD5E2",
    img: "https://lh3.googleusercontent.com/fEQ9Pf100_G8DvUiCSTJssOaP47NPXBHweml12VxWI6C1RKfUa8Qa9hc4MG2okyNHkPxMhgvjqUaBxjBMsK2I5Q7KmgygDMDeDACyQ=e365-pa-nu-s0",
    colStart: 2,
    rowStart: 3,
  },
  {
    title: "a plant decoder for sustainable agriculture",
    href: "https://x.company/projects/mineral",
    color: "#526517",
    img: "https://lh3.googleusercontent.com/ZxG7UMUD-FsZE28c0i338j8MiCn17uDdpi7Tm3voGqPuJFnrt_NoIDGJ6bZriNzU4zZ3Hnqg34NcWAobKCx50Zfzy5xEIVl2dFU=e365-pa-nu-s0",
    colStart: 3,
    rowStart: 3,
  },
  {
    title: "fuel from seawater",
    href: "https://x.company/projects/foghorn",
    color: "#4D938C",
    img: "https://lh3.googleusercontent.com/0mO_ye6zYJVy1EGqUfJlxeuSC97276M-yEWYKkP8oaC9ArFJ7CGnYFCQbehSB7U9OZu7iMujNk_qCZr5XyNi91gWWMGqAMH3Rg1j=e365-pa-nu-s0",
    colStart: 4,
    rowStart: 3,
  },
  {
    title: "Google Maps for electrons",
    href: "https://x.company/projects/tapestry",
    color: "#282C3D",
    img: "https://lh3.googleusercontent.com/2QAbtZgsrtnxB8fig8VbOVNN7MElFbvhfPOx3-WxA_AKMaMrS0hG_Stk_VNZezcM6umSG538_n0k0U9NzlBsBtcxqquus0h74fIA=e365-pa-nu-s0",
    colStart: 5,
    rowStart: 3,
  },
  {
    title: "superhuman hearing",
    href: "https://x.company/projects/iyo",
    color: "#9F7D44",
    img: "https://lh3.googleusercontent.com/b2TE1GoQZ2IyYVODLxhKV9wlQ3rkIctu-CZoM4izPb0JxH50Z2wHQD4Pb2IyiSdCPL20bcw42cbZwTDybhyJzUDP1L0ozzaEY27ejw=e365-pa-nu-s0",
    colStart: 6,
    rowStart: 3,
  },
];

const GridItem = styled.div`
  /* Allow manual placement to emulate the reference mosaic */
  grid-column: ${({ $col }) => ($col ? `${$col} / span 1` : "auto")};
  grid-row: ${({ $row }) => ($row ? `${$row} / span 1` : "auto")};

  /* On mobile, let items flow naturally in a single column */
  @media (max-width: 768px) {
    grid-column: auto;
    grid-row: auto;
  }
`;

const XProjectsGrid = () => {
  return (
    <Page>
      <Container>
        <Title>Projects grid (X inspired)</Title>
        <Grid>
          {TILES.map((t, i) => (
            <GridItem key={i} $col={t.colStart} $row={t.rowStart}>
              <TileLink href={t.href} target="_blank" rel="noreferrer">
                {t.img && <Img src={t.img} alt="" loading="lazy" />}
                <ColorOverlay $color={t.color}>
                  <Caption>{t.title}</Caption>
                </ColorOverlay>
              </TileLink>
            </GridItem>
          ))}
        </Grid>
      </Container>
    </Page>
  );
};

export default XProjectsGrid;


